import * as Sentry from '@sentry/react'

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN || 'https://3c758da8172769843bb67675985f7816@o4509955399417856.ingest.de.sentry.io/4509955420389457'
  
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    sendDefaultPii: true, // Send default PII data like IP addresses
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions will be recorded
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with an error will be recorded
    
    beforeSend(event) {
      // Filter out development errors
      if (import.meta.env.MODE === 'development') {
        console.log('Sentry Event:', event)
      }
      
      // Don't send events for cancelled requests
      if (event.exception?.values?.[0]?.value?.includes('AbortError')) {
        return null
      }
      
      // Filter out common third-party script errors
      const errorMessage = event.exception?.values?.[0]?.value || ''
      
      // Filter out shadowRoot errors (usually from browser extensions or third-party scripts)
      if (errorMessage.includes('shadowRoot') || errorMessage.includes('Cannot read properties of null')) {
        console.log('Filtered out shadowRoot error:', errorMessage)
        return null
      }
      
      // Filter out other common third-party script errors
      const filteredErrors = [
        'Script error',
        'Non-Error promise rejection captured',
        'ResizeObserver loop limit exceeded',
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        'ChunkLoadError',
        'Loading chunk',
        'Loading CSS chunk',
        'Loading module',
        'Uncaught SyntaxError',
        'Uncaught ReferenceError',
        'Uncaught TypeError'
      ]
      
      if (filteredErrors.some(filteredError => errorMessage.includes(filteredError))) {
        console.log('Filtered out common third-party error:', errorMessage)
        return null
      }
      
      return event
    },

    // Add user context automatically
    initialScope: {
      tags: {
        component: 'frontend',
        platform: 'web'
      }
    }
  })
}

// Helper functions for manual error tracking
export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach(key => {
        scope.setTag(key, context[key])
      })
    }
    Sentry.captureException(error)
  })
}

export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
  Sentry.captureMessage(message, level)
}

export const setUserContext = (user: { id: string; email: string; name: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  })
}

export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

// Track business-specific events
export const trackUserAction = (action: string, additional?: Record<string, any>) => {
  addBreadcrumb(`User ${action}`, 'user', additional)
}